import { Field, Form, useFormikContext } from "formik";
import React, { FC } from "react";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { getArrayBufferFromBlob } from "../../exporters/LicenseHelper";
import closeIcon from '../../images/close.svg';
import { getCertificateTypes } from "../../services/securityPolicyService";
import { requiredValidation } from "../../utilities/Validation";
import Label from "../label/label";
import PaginatedSearchableSelectField from "../selection-field/paginated-searchable-select-field";

interface ImportPemCertificateProps {
    onClose?: () => void;
}

const ImportPemCertificate: FC<ImportPemCertificateProps> = ({ onClose }) => {
    const { translate } = useLocale();
    const { setFieldValue } = useFormikContext<any>();

    const readFile = async (file) => {
        const arrayBuffer = await getArrayBufferFromBlob(file);
        const fileAsByteArray = Object.values(arrayBuffer);
        return fileAsByteArray;
    }

    /**
        * Allow the user to select the PEM Certificate Authority (CA).
        * @param event 
        */
    const onBrowsePemCaCert = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const fileContentBytes = await readFile(file);
        setFieldValue("caCertificate", file.name);
        setFieldValue("caCertificateFileContent", fileContentBytes);
    };

    /**
     * Allow the user to select the PEM Certificate Authority (CA).
     * @param event 
     */
    const onBrowsePemPrivateKey = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const fileContentBytes = await readFile(file);
        setFieldValue("privateKey", file.name);
        setFieldValue("privateKeyFileContent", fileContentBytes);
    };

    /**
     * Allow the user to select the PEM Certificate.
     * @param event 
     */
    const onBrowsePemCertificate = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }

        const fileContentBytes = await readFile(file);
        setFieldValue("pemCertificate", file.name);
        setFieldValue("pemCertificateFileContent", fileContentBytes);
    };

    return <Form style={{ width: '450px' }}>
        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
            <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.importPemCertificateTitle)}</p>
            <img src={closeIcon} onClick={onClose} className="cursor-pointer mr-2" />
        </div>
        <div className="flex flex-col  p-5 w-full">
            {/* PEM Certificate */}
            <div className="flex flex-row mt-2 ">
                <div className="flex flex-col w-full">
                    <Label htmlFor='pemCertificate' required
                        className="text-xs text-gray-600">{translate.byKey(TranslationKeys.pemCert)}:</Label>
                    <Field
                        disabled={true} // User needs to select the certificate instead of typing it in.
                        name="pemCertificate"
                        type="text"
                        validate={requiredValidation}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
                </div>
                <div className="flex flex-col flex-1 w-20 mt-5">
                    <div className='flex flex-row ml-2 px-4 p-1 rounded-lg shadow text-white text-xs  bg-sidemenu-focus cursor-pointer'>
                        <label htmlFor="pemCertificate" className="items-center">
                            {translate.byKey(TranslationKeys.browse)}
                        </label>
                        <input
                            type='file'
                            accept='.pem'
                            data-testid="btn_pemCertificate"
                            id='pemCertificate'
                            className='hidden'
                            onChange={(e) => onBrowsePemCertificate(e)} />
                    </div>
                </div>
            </div>

            {/* PEM CA certificate */}
            <div className="flex flex-row mt-2 ">
                <div className="flex flex-col w-full">
                    <Label htmlFor='caCertificate' required
                        className="text-xs text-gray-600">{translate.byKey(TranslationKeys.pemCaCertificate)}:</Label>
                    <Field
                        disabled={true} // User needs to select the CA certificate instead of typing it in.
                        name="caCertificate"
                        type="text"
                        validate={requiredValidation}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
                </div>
                <div className="flex flex-col w-20 mt-5">
                    <div className='flex flex-row ml-2 px-4 p-1 rounded-lg shadow text-white text-xs  bg-sidemenu-focus cursor-pointer'>
                        <label htmlFor="caCertificate" className="items-center">
                            {translate.byKey(TranslationKeys.browse)}
                        </label>
                        <input
                            type='file'
                            accept='.pem'
                            data-testid="btn_caCertificate"
                            id='caCertificate'
                            className='hidden'
                            onChange={(e) => onBrowsePemCaCert(e)} />
                    </div>
                </div>
            </div>

            {/* PEM private key */}
            <div className="flex flex-row mt-2 ">
                <div className="flex flex-col w-full">
                    <Label htmlFor='privateKey' required
                        className="text-xs text-gray-600">{translate.byKey(TranslationKeys.pemPrivateKey)}:</Label>
                    <Field
                        disabled={true} // User needs to select the private key instead of typing it in.
                        name="privateKey"
                        type="text"
                        validate={requiredValidation}
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
                </div>
                <div className="flex flex-col w-20 mt-5">
                    <div className='flex flex-row ml-2 px-4 p-1 rounded-lg shadow text-white text-xs  bg-sidemenu-focus cursor-pointer'>
                        <label htmlFor="privateKey" className="items-center">
                            {translate.byKey(TranslationKeys.browse)}
                        </label>
                        <input
                            type='file'
                            accept='.key'
                            data-testid="btn_privateKey"
                            id='privateKey'
                            className='hidden'
                            onChange={(e) => onBrowsePemPrivateKey(e)} />
                    </div>
                </div>
            </div>

            {/* Private key Password */}
            <div className="flex flex-row mt-2 ">
                <div className="flex flex-col w-full">
                    <Label htmlFor='password' className="text-xs text-gray-600">{translate.byKey(TranslationKeys.pemPassword)}:</Label>
                    <Field
                        name="password"
                        type="password"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
                </div>
            </div>

            {/* Alias */}
            <div className="flex flex-row mt-2 ">
                <div className="flex flex-col w-full">
                    <Label htmlFor='alias' required
                        className="text-xs text-gray-600">{translate.byKey(TranslationKeys.alias)}:</Label>
                    <Field
                        name="alias"
                        type="text"
                        className="flex flex-row items-center p-1 text-gray-800 text-sm bg-gray-input" />
                </div>
            </div>

            <div className="flex flex-col mt-2 ">
                <div className="flex flex-row justify-between">
                    <Label htmlFor='alias' required className="text-xs text-gray-600">{translate.byKey(TranslationKeys.certificateType)}:</Label>
                </div>
                <PaginatedSearchableSelectField
                    name={translate.byKey(TranslationKeys.type)}
                    key='certificateType'
                    accessor='certificateType'
                    dataMethod={getCertificateTypes}
                    searchAccessor={'typeName'}
                    required={true}
                    onSelect={(_, value) => {
                        setFieldValue('certificateTypeName', value?.label);
                        setFieldValue('certificateType', value?.value);
                    }} />
            </div>

            <div className="flex flex-row mt-4 space-x-2 justify-end bg-white">
                <button className="py-1 px-5 secondary secondary-inverse" type="button" onClick={onClose}>
                    {translate.byKey(TranslationKeys.cancel)}
                </button>
                <button
                    id='submitBtn'
                    className="secondary secondary-enabled py-1 px-5"
                    type='submit'>
                    {translate.byKey(TranslationKeys.submit)}
                </button>
            </div>
        </div>
    </Form>
}

export default ImportPemCertificate;